import { Box, Typography } from '@mui/material';
import { MapPin, Phone, Users, FileText } from 'lucide-react';
import { useCreatePath, useListContext, Link } from 'react-admin';

import { Account } from '../types';

const AccountGridCard = ({ record }: { record: Account }) => {
    const createPath = useCreatePath();
    const initials = record.name
        .split(/\s+/)
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const location = [record.city, record.stateAbbr].filter(Boolean).join(', ');

    return (
        <Link
            to={createPath({
                resource: 'accounts',
                id: record.id,
                type: 'show',
            })}
            underline="none"
            sx={{ display: 'block' }}
        >
            <Box
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    p: 2,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s, transform 0.15s',
                    '&:hover': {
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        transform: 'scale(1.02)',
                    },
                }}
            >
                {/* Header: Avatar + Name */}
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    {record.logo?.src ? (
                        <Box
                            component="img"
                            src={record.logo.src}
                            alt={record.name}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                objectFit: 'contain',
                                backgroundColor: '#ecfdf5',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor: '#ecfdf5',
                                color: '#047857',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                flexShrink: 0,
                            }}
                        >
                            {initials}
                        </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                color: '#0f172a',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {record.name}
                        </Typography>
                        {record.sector && (
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                }}
                            >
                                {record.sector}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Info rows */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        mb: 1.5,
                    }}
                >
                    {location && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <MapPin size={14} color="#94a3b8" />
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#475569',
                                }}
                            >
                                {location}
                            </Typography>
                        </Box>
                    )}
                    {record.phone_number && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Phone size={14} color="#94a3b8" />
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#475569',
                                }}
                            >
                                {record.phone_number}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Stats footer */}
                <Box
                    sx={{
                        borderTop: '1px solid #e2e8f0',
                        pt: 1.5,
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Users size={13} color="#94a3b8" />
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                fontWeight: 500,
                            }}
                        >
                            {record.nb_contacts ?? 0} Contacts
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <FileText size={13} color="#94a3b8" />
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: '#64748b',
                                fontWeight: 500,
                            }}
                        >
                            {record.nb_quotes ?? 0} Quotes
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Link>
    );
};

export const AccountCardGrid = () => {
    const { data, isPending } = useListContext<Account>();

    if (isPending || !data) return null;

    if (data.length === 0) {
        return (
            <Box py={4} textAlign="center">
                <Typography
                    sx={{
                        color: '#64748b',
                        fontSize: '0.875rem',
                    }}
                >
                    No accounts found
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            display="grid"
            gridTemplateColumns={{
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
            }}
            gap={2}
        >
            {data.map(record => (
                <AccountGridCard key={record.id} record={record} />
            ))}
        </Box>
    );
};
